import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../../../components/Form";
import InputMeasurement from "../../../../../../../components/Form/InputMeasurement";
import {
  DespatchShippingItem,
  DespatchShippingItemKeys,
} from "../../../../../../../models/despatch";
import { DecimalFormatType } from "../../../../../../../util/formatDecimalValue";
import { getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import { useShippingItemsMaintenance } from "../../hooks/useShippingItemsMaintenance";
import { StyledModal, Section, styles } from "./styles";

type Props = {
  onSubmit: (data: DespatchShippingItem) => void;
};

type Handles = {
  open: (data?: DespatchShippingItem) => void;
  close: () => void;
};

const ShippingItemsMaintenance: ForwardRefRenderFunction<Handles, Props> = (props, ref) => {
  const { onSubmit } = props;

  const { t: translate } = useTranslation();
  const classes = styles();
  const measurementWeight = getWeightMeasurement();

  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);

  const {
    fields,
    FieldName,
    onUpdateField,
    onUpdateFieldEvent,
    onPopulateFields,
    onResetFields,
    fieldHasError,
    getErrorByField,
    validateFields,
  } = useShippingItemsMaintenance();

  const onSubmitModal = () => {
    if (validateFields(fields)) {
      onSubmit(fields);
    }
  };

  const isUpdating = useMemo(() => fields?.id || fields?.uuid, [fields?.id, fields?.uuid]);

  const onOpenModal = useCallback((data?: DespatchShippingItem) => {
    if (data) onPopulateFields(data);
    setIsModalOpened(true);
  }, []);

  const onCloseModal = useCallback(() => {
    onResetFields();
    setIsModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <StyledModal
      open={isModalOpened}
      handleClose={onCloseModal}
      title={isUpdating ? translate("editShippingItem") : translate("newShippingItem")}
      showActionButtons
      dataCyTitle="despatch-shipping-items-maintenance-title"
      actionButtonProps={{
        textSave: isUpdating ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: onSubmitModal,
        onCancel: () => onCloseModal(),
      }}
      data-cy="despatch-shipping-items-maintenance-modal"
    >
      <Section data-cy="despatch-shipping-items-maintenance">
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} container direction="row" spacing={2}>
            <Grid item xs={3}>
              <Input
                name={FieldName.type}
                label={translate("type")}
                value={fields?.type || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("type")}
                errorMessage={getErrorByField("type")}
                required
              />
            </Grid>
            <Grid item xs={2}>
              <Input
                name={FieldName.volume}
                label={translate("quantity")}
                value={fields?.volume || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("volume")}
                errorMessage={getErrorByField("volume")}
                required
                errorMessageClass={classes.error}
                type="number"
              />
            </Grid>
          </Grid>

          <Grid item xs={12} container direction="row" spacing={2}>
            <Grid item xs={12}>
              <Input
                name={FieldName.description}
                label={translate("description")}
                value={fields?.description || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("description")}
                errorMessage={getErrorByField("description")}
                required
              />
            </Grid>
          </Grid>

          <Grid item xs={12} container direction="row" spacing={2}>
            <Grid item xs={3}>
              <Input
                name={FieldName.length}
                label={translate("length")}
                value={fields?.length || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("length")}
                errorMessage={getErrorByField("length")}
                type="number"
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                name={FieldName.width}
                label={translate("width")}
                value={fields?.width || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("width")}
                errorMessage={getErrorByField("width")}
                type="number"
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                name={FieldName.height}
                label={translate("height")}
                value={fields?.height || ""}
                onChange={onUpdateFieldEvent}
                error={fieldHasError("height")}
                errorMessage={getErrorByField("height")}
                type="number"
              />
            </Grid>
            <Grid item xs={3}>
              <InputMeasurement
                name={FieldName.weight}
                label={`${translate("weight")}`}
                value={fields?.weight || 0}
                change={(value: number) => onUpdateField(DespatchShippingItemKeys.WEIGHT, value)}
                error={fieldHasError("weight")}
                errorMessage={getErrorByField("weight")}
                measurement={measurementWeight}
                decimalPlacesCustom={DecimalFormatType.MAERSK}
              />
            </Grid>
          </Grid>
        </Grid>
      </Section>
    </StyledModal>
  );
};

export type { Handles as ShippingItemsMaintenanceHandles };
export default forwardRef(ShippingItemsMaintenance);
